import { uniqBy } from 'lodash';

import store from '@/store';
import { ZOOM_MEETING_SUB_TYPES, isEduSchCmpPlusContext } from '@/plans/ZOOM_MEETING/common/utils';
import { getAssignedLicenseQuantity, getMaxLicensesAddonPlan, getUnAssignLicenseQuantity } from '@/plans/ZOOM_ONE_BUNDLE/utils'
import { plansof } from '@/utils/chain';
import { planTypes, ZOOM_MEETING_EDU_PLUS_MAX_LICENSE, ZOOM_MEETING_EDU_PLUS_MIN_LICENSE }  from '@/common/constants';
import { translation } from '@/plugins/i18n';
import { $context } from '@/global-install';
import { dateTimeFormat } from '@/utils/dateformat';
import { isProductExcludedInShoppingCart } from '@/views/config/shopping-cart/persist/plan-remove';
import { ZmMessage } from '@/components/zoom-ui-plus/message/index';

export function checkSelfLicenseNum(min = ZOOM_MEETING_EDU_PLUS_MIN_LICENSE, max = ZOOM_MEETING_EDU_PLUS_MAX_LICENSE, equalAlert = false) {
  const isEduPlusInCart = plansof(store.getters.getSelectedPlans).type(planTypes.ZONE_EDU_PLUS).has();
  if (!isEduPlusInCart) return { message: '' };
  if(!window.$context?.is?.fromPersistentCart){
  if (!isEduSchCmpPlusContext || isProductExcludedInShoppingCart(store.getters.getSelectedPlans)) return { message: '' }
  }

  const { getSelectedPlans } = store.getters;
  const total = plansof(getSelectedPlans).type(planTypes.ZONE_EDU_PLUS).sum('license');

  if (equalAlert ? total >= max : total > max) {
    if(window.$context.is.edu2esc) return ''
    return { message: translation('billing.zone.max_of_plans', [ max, max + 1 ]) };
  }

  if (total < min) {
    return { message: translation('billing.zone_edu_plus.at_least_desc', [ min ]) };
  }
}

export function getMinimumSubjectAddon() {
  // get plans whose licenses amount is subject to base plan
  return getMaxLicensesAddonPlan(store.getters?.getUserCurrentSubscriptions)
}

export function getMinimumSubjectLicense() {
  // get plans whose licenses amount is subject to base plan
  const maxSubjectLicense = getMinimumSubjectAddon()?.cartInfo?.selected_number || 0;
  return Math.max(maxSubjectLicense, ZOOM_MEETING_EDU_PLUS_MIN_LICENSE)
}

export function getSelfTotalLicenseAmount() {
  const { getSelectedPlans, boughtPlans } = store.getters;

  const boughtSelf = plansof(boughtPlans).type(planTypes.ZONE_EDU_PLUS).get();
  const selectedSelf = plansof(getSelectedPlans).type(planTypes.ZONE_EDU_PLUS).get();

  return plansof(uniqBy([...selectedSelf, ...boughtSelf], 'plan_code')).clone().type(planTypes.ZONE_EDU_PLUS).sum('license');
}

export function checkAddonLicenseSubject2Base() {
  if (!isEduSchCmpPlusContext) return { message: '' }

  // get plans whose licenses amount is subject to base plan
  const { cartInfo, typeNameKey } = getMaxLicensesAddonPlan(store.getters.getUserCurrentSubscriptions) ?? {}
  const maxSubjectLicense = cartInfo?.selected_number || 0

  const totalAmount = getSelfTotalLicenseAmount()
  const min = getMinimumSubjectLicense()

  if (maxSubjectLicense >= ZOOM_MEETING_EDU_PLUS_MIN_LICENSE && totalAmount < maxSubjectLicense) {
    return {
      message: translation('billing.meeting_license_decrease_tips_need_decrease_addon', {
        product: translation(typeNameKey),
      }),
      min,
    };
  }

  if (totalAmount < min) {
    return {
      message: translation('billing.zobizplus.required_license_warning', [ min ]),
      min,
    }
  }

  return {
    message: '',
    min,
  };
}

export async function checkZoneEduPlusAddonLicense() {
  const { message } = checkAddonLicenseSubject2Base()
  if (message) {
    ZmMessage.error({ message, dangerouslyUseHTMLString: true });
    return Promise.reject();
  }
  return Promise.resolve()
}

export async function checkZoneEduPlusLicense() {
  let ret
  if ($context.is.buyNow || $context.is.basic2pro) {
    ret = checkSelfLicenseNum()
  }
  else if ($context.is.add2cart) {
    const boughtSelf = plansof(store.getters.boughtPlans).type(planTypes.ZONE_EDU_PLUS).get()
    if (boughtSelf?.length) {
      ret = checkAddonLicenseSubject2Base()
    } else {
      ret = checkSelfLicenseNum()
    }
  }

  if (ret?.message) {
    ZmMessage.error({ message: ret.message, dangerouslyUseHTMLString: true });
    return Promise.reject();
  }
  return Promise.resolve()
}

export function checkEduPlusDownSell() {
  let eduPlusExpireTime = null;
  if (isEduSchCmpPlusContext) {
    const getters = store.getters;
    const selectedPlans = plansof(getters.getSelectedPlans).type(planTypes.ZONE_EDU_PLUS).get();
    const boughtPlans = plansof(getters.boughtPlans).type(planTypes.ZONE_EDU_PLUS).get();

    boughtPlans.forEach(p => {
      const plan = selectedPlans.find(item => item.plan_code === p.plan_code);
      if (plan && p.cartInfo.selected_number > plan.cartInfo.selected_number) {
        eduPlusExpireTime = dateTimeFormat(p?.cartInfo?.planExpireTime);
      }
    });
  }

  return {
    eduPlusExpireTime,
  };
}

export function checkEduPlusAssignInfo() {
  const { assignInfo, getSelectedPlans } = store.getters;
  const selectedPlan = plansof(getSelectedPlans).type(planTypes.ZONE_EDU_PLUS).get()

  return getUnAssignLicenseQuantity({
    plans: selectedPlan,
    assignInfo,
  });
}

export function checkEduPlus2EduAssignInfo() {
  const { boughtPlans, getSelectedPlans, assignInfo } = store.getters;

  const assignedEduPlusLicenseQuantity = getAssignedLicenseQuantity(plansof(boughtPlans).type(planTypes.ZONE_EDU_PLUS).get(), assignInfo);
  const selectedPlan = plansof(getSelectedPlans).type(planTypes.ZOOM_MEETING).category(ZOOM_MEETING_SUB_TYPES.ZONE_EDU_SCH_CMP).get()

  return assignedEduPlusLicenseQuantity - selectedPlan[0].cartInfo.selected_number;
}
